import { getCanvas, type ApiError as CanvasApiError } from "@/services/api";
import { getParticipationCanvas } from "@/services/participationApi";
import { type CanvasData } from "@/services/apitypes";
import { canvasKeys } from "@/services/cacheKeys";
import { getCanvasViaPostMessage } from "@/services/postMessageCanvas";
import { useLanguageStore } from "@/stores/languageStore";
import { ApiLoadingState, useAuth } from "@canucci/base/auth";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CanvasContext } from "@/contexts/CanvasContext";
import { canvasQueryEnabled } from "@/contexts/canvasQueryEnabled";
import { useEffect } from "react";
import { parseMfaStepUpError } from "@canucci/base/auth/mfaStepUpError";

export function CanvasProvider({
  children,
  mode = "canvas",
}: {
  children: React.ReactNode;
  mode?: "canvas" | "participation";
}) {
  const params = useParams<{ canvasId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const externalId = params.canvasId;
  const isParticipation = mode === "participation";
  const isPreviewMode = externalId === "--preview--";
  const { user, loading: authLoading, setDeepLink } = useAuth();
  const authResolved = authLoading !== ApiLoadingState.loading;

  const langForApiQuery = useLanguageStore((state) => state.currentLanguage);

  const canvasResult = useQuery<CanvasData, CanvasApiError>({
    queryKey: isPreviewMode
      ? ["canvases", "preview"]
      : [...canvasKeys.detail(externalId!, langForApiQuery, isParticipation), user?.uid ?? "anonymous"],
    queryFn: async () => {
      if (isPreviewMode) {
        console.log("Fetching canvas data via postMessage (preview mode)");
        return getCanvasViaPostMessage(15000);
      } else if (isParticipation) {
        const token = user ? await user.getIdToken() : undefined;
        return getParticipationCanvas(externalId!, langForApiQuery, token);
      } else {
        console.log(`Fetching canvas data for ID: ${externalId}, Language: ${langForApiQuery || "initial_fetch"}`);
        let token: string | undefined = undefined;
        if (user) {
          token = await user.getIdToken();
        }
        return getCanvas(externalId!, langForApiQuery, false, token);
      }
    },
    enabled: isParticipation
      ? Boolean(externalId) && authResolved
      : canvasQueryEnabled(externalId, isPreviewMode, authLoading === ApiLoadingState.loading),
    retry: false,
    // Keep link-state in sync so archived canvases disappear without manual refresh.
    refetchInterval: isPreviewMode ? false : 5000,
  });

  useEffect(() => {
    if (!externalId || !canvasResult.error || !authResolved) return;
    if (canvasResult.error.status === 401) {
      setDeepLink(location.pathname + location.search);
      navigate("/login", { replace: true });
    } else if (canvasResult.error.status === 403 && !parseMfaStepUpError(canvasResult.error)) {
      navigate("/access-denied", { replace: true });
    }
  }, [
    authResolved,
    externalId,
    canvasResult.error,
    isParticipation,
    location.pathname,
    location.search,
    navigate,
    setDeepLink,
  ]);

  const value = {
    canvasResult,
    isParticipation,
    participationToken: isParticipation ? externalId : undefined,
    externalId,
  };

  return <CanvasContext.Provider value={value}>{children}</CanvasContext.Provider>;
}
